import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { PaginatedResponse, PaginationMeta } from '@marine/api-client/types/api';
import type { components } from '@marine/contracts/generated/api';
import type {
  Customer,
  CustomerBoat,
  CustomerFormData,
  CustomerSegmentCounts,
  Lead,
  Quote,
} from '../types/crm.types';
import type { Enquiry } from '../types/enquiry.types';

// D9-12 (#408) — typed against the regenerated contract request schemas
// rather than a hand-written shape, so a drift in the server's FormRequest
// bounds breaks the type check here instead of silently accepting a bad body.
export type CreateCustomerBoatRequest = components['schemas']['CreateCustomerBoatRequest'];
export type UpdateCustomerBoatRequest = components['schemas']['UpdateCustomerBoatRequest'];

export const customerApi = {
  /**
   * Get all customers with optional filters and pagination. Meta additionally
   * carries the per-segment folder totals (#1057) — optional in the type so a
   * stale cached payload from before the field existed stays assignable.
   */
  getCustomers: (params?: {
    page?: number;
    per_page?: number;
    search?: string;
    customer_type?: 'individual' | 'business';
    status?: 'active' | 'inactive' | 'lead';
    lifecycle_stage?: 'lead' | 'customer' | 'former_customer';
    owner_id?: string;
    has_boats?: boolean;
    deleted?: 'with' | 'only';
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<
    PaginatedResponse<Customer> & {
      meta: PaginationMeta & { segment_counts?: CustomerSegmentCounts };
    }
  > => tenantApi.getPaginated<Customer>('/customers', { params }),

  /** Get single customer by ID with relations */
  getCustomer: (id: string) =>
    tenantApi.getOne<Customer>(`/customers/${id}`, {
      params: { with_deleted: true },
    }),

  /** Create new customer */
  createCustomer: (customerData: CustomerFormData) =>
    tenantApi.postOne<Customer>('/customers', customerData),

  /** Update existing customer */
  updateCustomer: (id: string, customerData: Partial<CustomerFormData>) =>
    tenantApi.putOne<Customer>(`/customers/${id}`, customerData),

  /** Delete customer (soft delete) */
  deleteCustomer: (id: string) =>
    tenantApi.deleteOne(`/customers/${id}`),

  /** Restore a soft-deleted customer */
  restoreCustomer: (id: string) =>
    tenantApi.postOne<Customer>(`/customers/${id}/restore`),

  /** Get customer's leads (paginated) */
  getCustomerLeads: (id: string, params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }) => tenantApi.getPaginated<Lead>(`/customers/${id}/leads`, { params }),

  /** Get customer's quotes (paginated) */
  getCustomerQuotes: (id: string, params?: {
    page?: number;
    per_page?: number;
    status?: string;
  }) => tenantApi.getPaginated<Quote>(`/customers/${id}/quotes`, { params }),

  /** Get customer's inquiries (paginated, newest first) — #1091 */
  getCustomerEnquiries: (id: string, params?: {
    page?: number;
    per_page?: number;
  }) => tenantApi.getPaginated<Enquiry>(`/customers/${id}/enquiries`, { params }),

  /** Get customer's boats (paginated) */
  getCustomerBoats: (id: string, params?: {
    page?: number;
    per_page?: number;
    with_deleted?: boolean;
  }) => tenantApi.getPaginated<CustomerBoat>(`/customers/${id}/boats`, { params }),

  /**
   * D9-12 walk-in entry — HIN optional at create, refused inline via 409
   * `HIN_ALREADY_ON_FILE` when it collides with another customer boat or an
   * inventory boat. `customer_id`/`location_id`/`status`/`inventory_boat_id`
   * are server-derived and absent from the request type (T-09-04).
   */
  createBoat: (customerId: string, data: CreateCustomerBoatRequest) =>
    tenantApi.postOne<CustomerBoat>(`/customers/${customerId}/boats`, data),

  /** D9-11 add-the-HIN-later path — partial update of an existing customer boat. */
  updateBoat: (boatId: string, data: UpdateCustomerBoatRequest) =>
    tenantApi.patchOne<CustomerBoat>(`/customer-boats/${boatId}`, data),

  /** Get customer statistics (aggregated data) */
  getCustomerStats: (id: string) =>
    tenantApi.getOne<{
      total_leads: number;
      active_leads: number;
      total_quotes: number;
      lifetime_value: number;
      last_contact_date?: string;
    }>(`/customers/${id}/stats`),

  /** #974 — record picker. Param is `query` (SearchCustomersRequest), not `search`. */
  search: (query: string) =>
    tenantApi.getPaginated<Customer>('/customers-search', { params: { query, per_page: 6 } }),
};
